import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthContext.js'
import { ApiError } from '../api/client.js'

/**
 * Stage 3 §2.1: "First login for a newly created user → forced password reset
 * on first login." Rendered by App.tsx whenever user.must_reset_password is
 * true, in place of everything else — there is nowhere else to navigate to
 * until this completes, matching the backend's blockUntilPasswordReset guard.
 */
export function ChangePasswordPage() {
  const { t } = useTranslation()
  const { changePassword, logout, user } = useAuth()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match')
      return
    }
    setSubmitting(true)
    try {
      await changePassword(currentPassword, newPassword)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to change password')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="tf-login-shell">
      <div className="tf-login-card">
        <span className="tf-logo">{t('app.name')}</span>
        <h1 className="tf-login-title">Set a new password</h1>
        <p className="tf-note">
          {user?.name}, this account was created with a temporary password. Choose your own
          before continuing.
        </p>

        <form onSubmit={handleSubmit} className="tf-form">
          <label className="tf-field">
            <span>Temporary / current password</span>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>
          <label className="tf-field">
            <span>New password</span>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <small>At least 8 characters, including a number.</small>
          </label>
          <label className="tf-field">
            <span>Confirm new password</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>

          {error && <p className="tf-error">{error}</p>}

          <button type="submit" className="tf-button-primary" disabled={submitting}>
            {submitting ? t('common.loading') : 'Set password'}
          </button>
          <button type="button" className="tf-button-link" onClick={() => void logout()}>
            {t('common.cancel')}
          </button>
        </form>
      </div>
    </div>
  )
}
